import React, { useState, useEffect, useCallback } from "react";
import {
  Table, Card, Tag, Button, Drawer, Spin, Empty, Badge, message,
  Input, Select, Avatar, Form, Row, Col, Progress, Tooltip, Space,
} from "antd";
import {
  UserOutlined, ExclamationCircleOutlined, StarOutlined,
  CommentOutlined, CheckCircleOutlined, QuestionCircleOutlined,
  LineChartOutlined, EyeOutlined, FileTextOutlined,
  SearchOutlined, FilterOutlined, TeamOutlined,
  RiseOutlined, FireOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { API_CONST } from "../../../const";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

const { TextArea } = Input;
const { Option } = Select;

const Wrapper = styled.div`
  .page-header { margin-bottom: 24px; }
  .page-header h2 { color: #014c75; font-size: 22px; font-weight: 700; margin: 0; }
  .page-header p { color: #888; margin: 4px 0 0; }

  .stat-card {
    border-radius: 12px;
    border: none;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  }

  .perf-table .ant-table-thead > tr > th {
    background: #f8fafc;
    font-weight: 600;
    font-size: 12px;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    border-bottom: 2px solid #e8edf3;
  }

  .perf-table .ant-table-tbody > tr > td {
    vertical-align: middle;
    padding: 14px 16px;
  }

  .perf-table .ant-table-tbody > tr:hover > td {
    background: #f5f9ff !important;
  }

  .feedback-card { border-radius: 8px; margin-bottom: 12px; border: 1px solid #e6f0f8; }
  .response-thread { margin-top: 12px; padding-top: 12px; border-top: 1px solid #f0f0f0; }
  .response-item { padding: 8px 12px; background: #f8fbfe; border-radius: 6px; margin-bottom: 6px; }
`;

const SUBTYPE_COLOR = { concern: "error", warning: "orange", behavioral_recognition: "success", general: "processing" };
const SUBTYPE_LABEL = { concern: "Concern", warning: "Warning", behavioral_recognition: "Behavioral / Recognition", general: "General" };
const BUCKET_LABEL = { need_more_time: "Need More Time", not_meeting: "Not Meeting", meets_expectations: "Meets Expectations", impressive: "Impressive", excellent: "Excellent" };
const BUCKET_COLOR = { need_more_time: "default", not_meeting: "error", meets_expectations: "processing", impressive: "success", excellent: "gold" };
const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const scoreColor = (score) => {
  if (score === null || score === undefined) return "#aaa";
  if (score >= 80) return "#52c41a";
  if (score >= 50) return "#1677ff";
  if (score >= 30) return "#fa8c16";
  return "#ff4d4f";
};

const FeedbackItem = ({ item, onRespond, respondingId }) => {
  const [showResponse, setShowResponse] = useState(false);
  const [form] = Form.useForm();

  const handleRespond = async (type) => {
    try {
      const values = await form.validateFields();
      onRespond(item.id, { responseType: type, content: values.content });
      form.resetFields();
      setShowResponse(false);
    } catch { }
  };

  return (
    <Card className="feedback-card" size="small">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          {item.feedbackType === "instant" && (
            <>
              <Tag color={SUBTYPE_COLOR[item.instantSubType] || "default"} style={{ marginBottom: 4 }}>
                {SUBTYPE_LABEL[item.instantSubType] || item.instantSubType}
              </Tag>
              {item.severity && (
                <Tag color={item.severity === "high" ? "red" : item.severity === "medium" ? "orange" : "default"}>
                  {item.severity.toUpperCase()}
                </Tag>
              )}
            </>
          )}
          {item.feedbackType === "monthly" && (
            <Tag color="blue" style={{ marginBottom: 4 }}>
              Monthly Review — {MONTH_NAMES[(item.month || 1) - 1]} {item.year}
            </Tag>
          )}
          {item.feedbackType === "contract_end" && (
            <Tag color="purple" style={{ marginBottom: 4 }}>Contract End Evaluation</Tag>
          )}
          <div style={{ fontSize: 13, color: "#333", marginTop: 4 }}>
            {item.title && <strong>{item.title} </strong>}
            {item.description || item.comments || ""}
          </div>
          {item.ratingBucket && (
            <div style={{ marginTop: 6 }}>
              <Tag color={BUCKET_COLOR[item.ratingBucket]}>{BUCKET_LABEL[item.ratingBucket]}</Tag>
              {item.overallScore !== null && item.overallScore !== undefined && (
                <span style={{ fontSize: 13, color: "#014c75", fontWeight: 600 }}> {item.overallScore}%</span>
              )}
            </div>
          )}
        </div>
        <div style={{ textAlign: "right", fontSize: 12, color: "#aaa", minWidth: 90 }}>
          <Tag color={item.status === "open" ? "red" : "default"}>
            {item.status === "open" ? "Open" : item.status === "acknowledged_by_partner" ? "Acknowledged" : "Closed"}
          </Tag>
          <div style={{ marginTop: 4 }}>{new Date(item.createdAt).toLocaleDateString("en-IN")}</div>
        </div>
      </div>

      {item.responses && item.responses.length > 0 && (
        <div className="response-thread">
          {item.responses.map(r => (
            <div key={r.id} className="response-item">
              <div style={{ fontSize: 12, color: "#888" }}>
                <strong>{r.respondedByRole === "admin" ? "Admin" : "Partner"}</strong>
                {" · "}{r.responseType === "query" ? "Query" : r.responseType === "acknowledgment" ? "Acknowledged" : "Note"}
                {" · "}{new Date(r.createdAt).toLocaleDateString("en-IN")}
              </div>
              <div style={{ fontSize: 13, marginTop: 2 }}>{r.content}</div>
            </div>
          ))}
        </div>
      )}

      {item.status === "open" && (
        <div style={{ marginTop: 8 }}>
          {!showResponse ? (
            <div style={{ display: "flex", gap: 8 }}>
              <Button
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => onRespond(item.id, { responseType: "acknowledgment", content: "Acknowledged." })}
              >
                Acknowledge
              </Button>
              <Button size="small" icon={<QuestionCircleOutlined />} onClick={() => setShowResponse(true)}>
                Raise Query
              </Button>
            </div>
          ) : (
            <div style={{ marginTop: 8 }}>
              <Form form={form}>
                <Form.Item name="content" rules={[{ required: true, message: "Please enter your query" }]}>
                  <TextArea rows={2} placeholder="Describe your query or request for more details..." maxLength={1000} />
                </Form.Item>
              </Form>
              <div style={{ display: "flex", gap: 8 }}>
                <Button size="small" type="primary" style={{ background: "#014c75" }} onClick={() => handleRespond("query")}>
                  Send Query
                </Button>
                <Button size="small" onClick={() => { setShowResponse(false); form.resetFields(); }}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

const TalentPerformance = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTalent, setSelectedTalent] = useState(null);
  const [talentFeedback, setTalentFeedback] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [respondingId, setRespondingId] = useState(null);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState(null);
  const [page, setPage] = useState(1);
  const pageSize = 15;

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await axios.get(API_CONST.PERFORMANCE_FEEDBACK_TALENT_SUMMARY, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSummary(res.data.talents || []);
    } catch (e) {
      message.error("Failed to load performance summary");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const openTalentDrawer = async (talent) => {
    setSelectedTalent(talent);
    setDrawerOpen(true);
    setFeedbackLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await axios.get(`${API_CONST.PERFORMANCE_FEEDBACK_TALENT}/${talent.talentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTalentFeedback(res.data.feedback || []);
    } catch (e) {
      message.error("Failed to load feedback");
    } finally {
      setFeedbackLoading(false);
    }
  };

  const handleRespond = async (feedbackId, data) => {
    setRespondingId(feedbackId);
    try {
      const token = localStorage.getItem("authToken");
      await axios.post(`${API_CONST.PERFORMANCE_FEEDBACK}/${feedbackId}/response`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      message.success(data.responseType === "acknowledgment" ? "Feedback acknowledged" : "Query raised successfully");
      const res = await axios.get(`${API_CONST.PERFORMANCE_FEEDBACK_TALENT}/${selectedTalent.talentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTalentFeedback(res.data.feedback || []);
      fetchSummary();
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to submit response");
    } finally {
      setRespondingId(null);
    }
  };

  const groupByHiringRecord = (items) => {
    const groups = {};
    for (const item of items) {
      const key = item.hiringRecordId;
      if (!groups[key]) groups[key] = { hiringRecord: item.hiringRecord, items: [] };
      groups[key].items.push(item);
    }
    return Object.values(groups);
  };

  // Computed metrics
  const totalTalents = summary.length;
  const totalOpenIssues = summary.reduce((s, r) => s + (r.openIssues || 0), 0);
  const scored = summary.filter(r => r.avgScore !== null && r.avgScore !== undefined);
  const overallAvg = scored.length > 0
    ? Math.round(scored.reduce((s, r) => s + r.avgScore, 0) / scored.length)
    : null;
  const recentActivity = summary.filter(r =>
    r.lastFeedbackDate && new Date(r.lastFeedbackDate) > new Date(Date.now() - 30 * 86400000)
  ).length;

  const filtered = summary.filter(r => {
    const name = r.talent?.name || "";
    const role = r.talent?.role || "";
    const matchSearch = !search
      || name.toLowerCase().includes(search.toLowerCase())
      || role.toLowerCase().includes(search.toLowerCase())
      || (r.contracts || []).some(c => (c.jobId || "").toLowerCase().includes(search.toLowerCase()))
      || (r.contracts || []).some(c => (c.contractId || "").toLowerCase().includes(search.toLowerCase()));
    const matchFilter = !filterStatus
      || (filterStatus === "with_issues" ? r.openIssues > 0
        : filterStatus === "no_feedback" ? r.totalFeedback === 0
        : filterStatus === "has_feedback" ? r.totalFeedback > 0
        : true);
    return matchSearch && matchFilter;
  });

  const grouped = groupByHiringRecord(talentFeedback);

  const columns = [
    {
      title: "Talent",
      key: "talent",
      width: 210,
      render: (_, r) => (
        <div
          style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}
          onClick={() => navigate(`/partner/talent-performance/${r.talentId}`)}
        >
          <Avatar
            size={38}
            icon={<UserOutlined />}
            style={{ background: "linear-gradient(135deg, #014c75, #0288c7)", flexShrink: 0 }}
          />
          <div>
            <div style={{ fontWeight: 600, color: "#014c75", fontSize: 13, lineHeight: 1.3 }}>
              {r.talent?.name || "—"}
            </div>
            <div style={{ fontSize: 11, color: "#999", marginTop: 1 }}>{r.talent?.role || "—"}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Job ID",
      key: "jobIds",
      width: 140,
      render: (_, r) => {
        const list = (r.contracts || []).filter(c => c.jobId);
        if (!list.length) return <span style={{ color: "#ccc", fontSize: 12 }}>—</span>;
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {list.map((c, i) => (
              <Tooltip key={i} title={c.jobId}>
                <Tag
                  icon={<FileTextOutlined />}
                  color="blue"
                  style={{ borderRadius: 6, fontSize: 11, margin: 0, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis" }}
                >
                  {c.jobId}
                </Tag>
              </Tooltip>
            ))}
          </div>
        );
      },
    },
    {
      title: "Contract ID",
      key: "contractIds",
      width: 150,
      render: (_, r) => {
        const list = (r.contracts || []).filter(c => c.contractId);
        if (!list.length) return <span style={{ color: "#ccc", fontSize: 12 }}>—</span>;
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {list.map((c, i) => (
              <Tooltip key={i} title={c.contractId}>
                <Tag
                  color="geekblue"
                  style={{ borderRadius: 6, fontSize: 11, margin: 0, maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis" }}
                >
                  {c.contractId}
                </Tag>
              </Tooltip>
            ))}
          </div>
        );
      },
    },
    {
      title: "Feedback",
      key: "feedback",
      width: 130,
      render: (_, r) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <div
            style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              padding: "2px 10px", borderRadius: 20,
              background: r.totalFeedback > 0 ? "#e6f4ff" : "#f5f5f5",
              border: `1px solid ${r.totalFeedback > 0 ? "#91caff" : "#e0e0e0"}`,
            }}
          >
            <CommentOutlined style={{ fontSize: 10, color: r.totalFeedback > 0 ? "#1677ff" : "#bbb" }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: r.totalFeedback > 0 ? "#1677ff" : "#bbb" }}>
              {r.totalFeedback}
            </span>
          </div>
          {r.openIssues > 0 && (
            <Tooltip title={`${r.openIssues} open issue${r.openIssues > 1 ? "s" : ""}`}>
              <div
                style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  padding: "2px 8px", borderRadius: 20,
                  background: "#fff2f0", border: "1px solid #ffa39e",
                }}
              >
                <ExclamationCircleOutlined style={{ fontSize: 10, color: "#ff4d4f" }} />
                <span style={{ fontSize: 11, fontWeight: 600, color: "#ff4d4f" }}>{r.openIssues}</span>
              </div>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: "Avg Score",
      key: "avgScore",
      width: 90,
      align: "center",
      render: (_, r) => r.avgScore !== null && r.avgScore !== undefined ? (
        <Tooltip title={`${r.avgScore}% average performance score`}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Progress
              type="circle"
              percent={r.avgScore}
              size={38}
              strokeWidth={8}
              strokeColor={scoreColor(r.avgScore)}
              trailColor="#f0f0f0"
              format={p => (
                <span style={{ fontSize: 9, fontWeight: 700, color: scoreColor(r.avgScore) }}>
                  {p}%
                </span>
              )}
            />
          </div>
        </Tooltip>
      ) : (
        <div style={{ textAlign: "center" }}>
          <span style={{ fontSize: 11, color: "#bbb", padding: "2px 8px", background: "#fafafa", borderRadius: 20, border: "1px solid #f0f0f0" }}>
            No data
          </span>
        </div>
      ),
    },
    {
      title: "Last Activity",
      key: "lastActivity",
      width: 110,
      render: (_, r) => r.lastFeedbackDate ? (
        <span style={{ fontSize: 12, color: "#666" }}>
          {new Date(r.lastFeedbackDate).toLocaleDateString("en-IN")}
        </span>
      ) : (
        <span style={{ fontSize: 11, color: "#ccc", fontStyle: "italic" }}>No activity</span>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 80,
      render: (_, r) => (
        <Space size={4} onClick={e => e.stopPropagation()}>
          <Tooltip title="View feedback">
            <Button
              size="small"
              icon={<EyeOutlined />}
              style={{ color: "#014c75", borderColor: "#b3cfe0" }}
              onClick={() => openTalentDrawer(r)}
            />
          </Tooltip>
          <Tooltip title="Full report">
            <Button
              size="small"
              icon={<LineChartOutlined />}
              onClick={() => navigate(`/partner/talent-performance/${r.talentId}`)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Wrapper>
      <div className="page-header">
        <h2>Talent Performance</h2>
        <p>Review and respond to performance feedback for your placed talents</p>
      </div>

      {/* Overview Metrics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {[
          {
            title: "Bench Talents",
            value: totalTalents,
            icon: <TeamOutlined />,
            bg: "#f0f7ff",
            accent: "#014c75",
            iconBg: "#014c75",
          },
          {
            title: "Open Issues",
            value: totalOpenIssues,
            icon: <ExclamationCircleOutlined />,
            bg: totalOpenIssues > 0 ? "#fff2f0" : "#f6ffed",
            accent: totalOpenIssues > 0 ? "#ff4d4f" : "#52c41a",
            iconBg: totalOpenIssues > 0 ? "#ff4d4f" : "#52c41a",
          },
          {
            title: "Avg Performance",
            value: overallAvg !== null ? `${overallAvg}%` : "—",
            icon: <RiseOutlined />,
            bg: "#f9f0ff",
            accent: scoreColor(overallAvg),
            iconBg: "#7c3aed",
          },
          {
            title: "Active Last 30 Days",
            value: recentActivity,
            icon: <FireOutlined />,
            bg: "#fffbe6",
            accent: "#fa8c16",
            iconBg: "#fa8c16",
          },
        ].map((s, i) => (
          <Col xs={24} sm={12} md={6} key={i}>
            <Card className="stat-card" bodyStyle={{ padding: "16px 20px" }} style={{ background: s.bg }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{
                  width: 42, height: 42, borderRadius: 12,
                  background: s.iconBg,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0, opacity: 0.9,
                }}>
                  <span style={{ color: "#fff", fontSize: 18 }}>{s.icon}</span>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "#888", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    {s.title}
                  </div>
                  <div style={{ fontSize: 26, fontWeight: 700, color: s.accent, lineHeight: 1.2, marginTop: 2 }}>
                    {loading ? "—" : s.value}
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Table Card */}
      <Card
        bordered={false}
        style={{ borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
        bodyStyle={{ padding: 0 }}
      >
        <div style={{
          display: "flex", gap: 10, padding: "16px 20px 12px",
          borderBottom: "1px solid #f0f0f0", flexWrap: "wrap",
          alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Input
              placeholder="Search by name, role, job or contract ID…"
              prefix={<SearchOutlined style={{ color: "#bbb" }} />}
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              style={{ width: 280, borderRadius: 8, fontSize: 13 }}
              allowClear
            />
            <Select
              placeholder={<span><FilterOutlined style={{ marginRight: 6 }} />Filter</span>}
              value={filterStatus}
              onChange={v => { setFilterStatus(v); setPage(1); }}
              style={{ width: 180, borderRadius: 8 }}
              allowClear
            >
              <Option value="with_issues">With Open Issues</Option>
              <Option value="has_feedback">Has Feedback</Option>
              <Option value="no_feedback">No Feedback Yet</Option>
            </Select>
          </div>
          <div style={{ fontSize: 12, color: "#999" }}>
            {filtered.length} talent{filtered.length !== 1 ? "s" : ""}
          </div>
        </div>

        <Table
          className="perf-table"
          dataSource={filtered}
          columns={columns}
          rowKey="talentId"
          loading={loading}
          style={{ padding: "0 4px" }}
          pagination={{
            current: page,
            pageSize,
            total: filtered.length,
            onChange: p => setPage(p),
            showTotal: (total, range) => `${range[0]}–${range[1]} of ${total}`,
            showSizeChanger: false,
            style: { padding: "12px 16px" },
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No performance feedback available yet. Feedback will appear here once customers submit reviews for your placed talents."
                style={{ padding: "40px 0" }}
              />
            ),
          }}
          onRow={record => ({
            onClick: () => openTalentDrawer(record),
            style: { cursor: "pointer" },
          })}
        />
      </Card>

      {/* Feedback Drawer */}
      <Drawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setSelectedTalent(null); setTalentFeedback([]); }}
        title={
          selectedTalent && (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Avatar
                size={44}
                icon={<UserOutlined />}
                style={{ background: "linear-gradient(135deg, #014c75, #0288c7)", flexShrink: 0 }}
              />
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: "#014c75", lineHeight: 1.3 }}>
                  {selectedTalent.talent?.name}
                </div>
                <div style={{ fontSize: 12, color: "#999" }}>{selectedTalent.talent?.role}</div>
              </div>
            </div>
          )
        }
        extra={
          selectedTalent && (
            <Button
              size="small"
              icon={<LineChartOutlined />}
              onClick={() => {
                setDrawerOpen(false);
                navigate(`/partner/talent-performance/${selectedTalent.talentId}`);
              }}
            >
              Full Report
            </Button>
          )
        }
        width={620}
        destroyOnClose
      >
        {selectedTalent && (
          <Row gutter={12} style={{ marginBottom: 20 }}>
            {[
              {
                label: "Total Feedback",
                value: selectedTalent.totalFeedback,
                color: "#014c75",
                bg: "#f0f7ff",
              },
              {
                label: "Open Issues",
                value: selectedTalent.openIssues,
                color: selectedTalent.openIssues > 0 ? "#ff4d4f" : "#52c41a",
                bg: selectedTalent.openIssues > 0 ? "#fff2f0" : "#f6ffed",
              },
              {
                label: "Avg Score",
                value: selectedTalent.avgScore !== null && selectedTalent.avgScore !== undefined
                  ? `${selectedTalent.avgScore}%`
                  : "—",
                color: scoreColor(selectedTalent.avgScore),
                bg: "#f9f0ff",
              },
            ].map((s, i) => (
              <Col span={8} key={i}>
                <div style={{
                  textAlign: "center", borderRadius: 10, padding: "14px 8px",
                  background: s.bg, border: "1px solid rgba(0,0,0,0.04)",
                }}>
                  <div style={{ fontSize: 11, color: "#aaa", marginBottom: 4, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                    {s.label}
                  </div>
                  <div style={{ fontSize: 26, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.value}</div>
                </div>
              </Col>
            ))}
          </Row>
        )}

        {feedbackLoading ? (
          <div style={{ textAlign: "center", padding: 60 }}><Spin /></div>
        ) : talentFeedback.length === 0 ? (
          <Empty description="No feedback found" />
        ) : (
          grouped.map((group, gi) => (
            <div key={gi} style={{ marginBottom: 20 }}>
              <div style={{
                padding: "8px 12px",
                background: "#f0f5ff",
                borderRadius: 6,
                marginBottom: 10,
                fontSize: 13,
                fontWeight: 600,
                color: "#014c75",
              }}>
                Contract {group.hiringRecord?.id ? `· ${group.hiringRecord.id}` : ""}
                {group.hiringRecord?.onboardingDate && (
                  <span style={{ fontWeight: 400, color: "#888", marginLeft: 8 }}>
                    Onboarded: {new Date(group.hiringRecord.onboardingDate).toLocaleDateString("en-IN")}
                  </span>
                )}
              </div>
              {group.items.map(item => (
                <FeedbackItem key={item.id} item={item} onRespond={handleRespond} respondingId={respondingId} />
              ))}
            </div>
          ))
        )}
      </Drawer>
    </Wrapper>
  );
};

export default TalentPerformance;
